<template>
  <div class="border-b border-gray-200 py-8 px-14">
    <p class="font-bold text-xl">
      <icon name="chat-dots" class="w-6 h-6" />
      {{ $t('settings.channel.title') }}
    </p>
  </div>
  <div class="flex h-full">
    <div class="w-1/2 h-full bg-gray-50 py-10 px-14 border-r border-gray-200">
      <div v-if="currentIssuerInfo" class="flex items-center mb-10">
        <div class="relative w-14 h-14 mr-3">
          <img
            :src="currentIssuerInfo.profileImage"
            alt=""
            class="absolute top-0 left-0 border bg-gray-200 w-14 h-14 my-auto rounded-full mr-3"
          />
        </div>
        <div>
          <p class="w-full font-medium break-words line-clamp-1">
            {{ currentIssuerInfo.name }}
          </p>
          <p class="text-xs text-gray-500">
            {{ currentIssuerInfo.membersCount }}
            {{ $t('navigation.membersTxt') }}
          </p>
        </div>
      </div>
      <p class="font-bold text-lg mb-4">
        {{ $t('settings.channel.newPost') }}
      </p>
      <div class="relative">
        <textarea
          type="text"
          :placeholder="$t('settings.channel.writeNews')"
          v-model="message"
          class="border border-gray-300 rounded-md px-4 py-4 mb-4 w-full h-48"
        />
        <div
          class="absolute bottom-6 left-1 text-sm px-2 py-1 rounded-md cursor-pointer"
          @click="showImageHandler = true"
        >
          <icon name="image" class="w-5 h-5 fill-gray-500" />
        </div>
        <div class="absolute bottom-2.5 left-6 flex items-center p-5">
          <p class="text-sm text-gray-500">
            {{ 350 - (message ? message.length : 0) }}
          </p>
        </div>
      </div>
      <div v-if="imagePreview" class="w-2/3 mb-4">
        <img
          :src="imagePreview"
          alt=""
          class="w-full h-full aspect-[1/0.7] object-cover rounded-md"
        />
      </div>
      <div class="flex justify-end">
        <button
          class="bg-blue-500 text-white text-right px-14 py-2 rounded-md"
          :disabled="isLoading"
          @click="publishToChannel"
        >
          {{ $t('settings.channel.publishButton') }}
        </button>
      </div>
    </div>
    <div class="w-1/2 h-full">
      <div
        v-if="!isLoading"
        class="flex justify-between items-center py-5 px-7 border-b border-gray-200"
      >
        <div class="flex items-center">
          <p class="text-gray-500 text-md flex items-center mr-4">
            <icon name="chat-dots" class="w-5 h-5 fill-gray-500 mr-1" />
            {{ channelMessageCount }} {{ $t('settings.channel.posts') }}
          </p>
          <!-- <p class="text-gray-500 text-md ml-2">
            <icon name="smile" class="w-5 h-5 fill-gray-500 mr-1" />
            {{
              channelMessages.reduce(
                (acc, message) => acc + message.reactions.length,
                0
              )
            }}
            reactions
          </p> -->
        </div>
      </div>
      <div class="flex flex-col h-full gap-5 py-5 px-7 overflow-y-scroll">
        <div v-if="isLoading" class="flex items-center p-10">
          <Vue3Lottie
            :animationLink="require('/assets/images/loader-round.jpg')"
            :height="50"
            :width="50"
            :speed="1.5"
          />
        </div>
        <div
          v-for="message in channelMessages"
          class="bg-gray-50 border border-gray-200 rounded-md"
          :key="message.id"
        >
          <div class="py-5 px-7">
            <p class="text-right text-gray-500 text-sm mb-2">
              {{ message.created.replace('T', ' ').split('.')[0] }}
            </p>
            <div
              v-if="message.message.attachments"
              class="mb-2"
              style="max-width: 500px"
            >
              <img
                :src="message.message.attachments[0].fileUrl"
                alt=""
                class="w-full h-full aspect-[1/0.7] object-cover rounded-lg"
              />
            </div>
            <p
              v-html="
                message.message.text.replace(
                  /(https?:\/\/[^\s]+)/g,
                  '<a href=\'$1\' target=\'_blank\' class=\'text-blue-500 hover:underline\'>$1</a>'
                )
              "
              class="mb-2"
            ></p>
            <div
              v-if="message.reactions.length > 0"
              class="flex flex-wrap w-fit items-center gap-1 bg-gray-100 border border-gray-300 rounded-2xl px-2 py-1"
            >
              <p
                v-for="reaction in [...new Set(message.reactions)]"
                :key="reaction"
                class="text-gray-500 text-sm"
              >
                {{ reaction }}
              </p>
              <p
                v-if="
                  message.reactions.length !=
                  [...new Set(message.reactions)].length
                "
                class="text-gray-500 text-sm ml-1"
              >
                {{ message.reactions.length }}
              </p>
            </div>
          </div>
          <div
            class="flex items-center justify-end gap-2 border-t border-gray-200 mt-5 py-3 px-5"
          >
            <icon
              name="trash"
              class="w-5 h-5 fill-gray-500 cursor-pointer"
              @click="deleteMessage(message.eventId)"
            />
          </div>
        </div>
        <div
          v-if="!isLoading && displayLoadMore"
          class="flex items-center justify-center pb-5"
        >
          <button
            class="bg-blue-500 text-white text-right px-14 py-2 rounded-md"
            :disabled="isLoading"
            @click="loadMoreMessages"
          >
            {{ $t('settings.channel.loadMore') }}
          </button>
        </div>
      </div>
    </div>
    <ImageHandler
      v-if="showImageHandler"
      :noGallery="true"
      :noCrop="true"
      @select="
        (file, data) => {
          imagePreview = data;
          imageFile = file;
          showImageHandler = false;
        }
      "
      @close="showImageHandler = false"
    ></ImageHandler>
  </div>
</template>

<script>
import { ref, inject, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import ImageHandler from '../../components/ImageHandler.vue';

export default {
  setup() {
    const api = inject('api');
    const cookies = inject('cookies');
    const store = useStore();

    const currentIssuerDid = computed(() => store.getters.getCurrentIssuerDid);
    const currentIssuerInfo = computed(
      () => store.getters.getCurrentIssuerInfo
    );

    const message = ref('');
    const channelMessages = ref([]);
    const channelMessagesSize = ref(3);
    const channelMessagesBeforeDate = ref(new Date().toISOString());

    const showImageHandler = ref(false);
    const imageFile = ref(null);
    const imagePreview = ref(null);

    const displayLoadMore = ref(true);
    const isLoading = ref(false);

    const channelMessageCount = ref(0);
    watch(currentIssuerInfo, (newInfo) => {
      if (newInfo) {
        channelMessageCount.value = newInfo.channelMessageCount;
      }
    });

    async function publishToChannel() {
      isLoading.value = true;
      api
        .publishToChannel(
          {
            message: message.value,
          },
          imageFile.value
        )
        .then((res) => {
          message.value = '';
          imageFile.value = null;
          imagePreview.value = null;
          channelMessages.value = [];
          channelMessagesBeforeDate.value = new Date().toISOString();
          channelMessageCount.value++;
          getChannelMessages();
        });
    }

    async function loadMoreMessages() {
      channelMessagesBeforeDate.value = new Date(
        new Date(
          channelMessages.value[channelMessages.value.length - 1].created
        ).getTime() - 1
      ).toISOString();
      getChannelMessages();
    }

    async function getChannelMessages() {
      isLoading.value = true;
      api
        .getChannelMessages(
          channelMessagesSize.value,
          channelMessagesBeforeDate.value
        )
        .then((res) => {
          channelMessages.value = [...channelMessages.value, ...res.data];
          isLoading.value = false;
          if (res.data.length === 0) {
            displayLoadMore.value = false;
          }
        });
    }

    async function deleteMessage(eventId) {
      isLoading.value = true;
      api.deleteMessage(eventId).then((res) => {
        channelMessages.value = [];
        channelMessagesBeforeDate.value = new Date().toISOString();
        channelMessageCount.value--;
        getChannelMessages();
      });
    }

    getChannelMessages();

    return {
      publishToChannel,
      message,
      channelMessages,
      getChannelMessages,
      currentIssuerDid,
      currentIssuerInfo,
      cookies,
      deleteMessage,
      showImageHandler,
      imageFile,
      imagePreview,
      loadMoreMessages,
      isLoading,
      displayLoadMore,
      channelMessageCount,
    };
  },
  components: {
    ImageHandler,
  },
};
</script>
